import { useEffect, useMemo, useState } from 'react';
import { Building2, X } from 'lucide-react';
import Button from '../../../components/commoncomponents/Button';
import Card from '../../../components/commoncomponents/Card';
import ConfirmationModal from '../../../components/commoncomponents/ConfirmationModal';
import Table from '../../../components/commoncomponents/Table';
import TableActionButtons from '../../../components/commoncomponents/TableActionButtons';
import { createBranch, getBranches, updateBranch } from '../../../services/branchService';
import { createBusiness, deleteBusiness, updateBusiness } from '../../../services/businessService';
import { getBusinessTypes } from '../../../services/businessTypeService';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'under_construction', label: 'In Development' },
];

const PORTAL_ACCESS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'locked', label: 'Locked' },
  { value: 'blurred', label: 'Blurred' },
];

function BusinessList({ businesses, token, user, onBusinessCreated }) {
  const isSuperadmin = user?.user_type === 0;
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBusinessId, setEditingBusinessId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [createdSetup, setCreatedSetup] = useState(null);
  const [selectedBusinessForBranches, setSelectedBusinessForBranches] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchError, setBranchError] = useState('');
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [branchSubmitting, setBranchSubmitting] = useState(false);
  const [branchFormValues, setBranchFormValues] = useState({
    name: '',
    status: 'active',
  });
  const [businessTypes, setBusinessTypes] = useState([]);
  const [businessTypesLoading, setBusinessTypesLoading] = useState(true);
  const [businessTypesError, setBusinessTypesError] = useState('');
  const [formValues, setFormValues] = useState({
    name: '',
    system_type: '',
    status: 'active',
    portal_access: 'open',
    initial_branch_name: 'Main Branch',
  });

  useEffect(() => {
    let ignore = false;

    async function loadBusinessTypes() {
      setBusinessTypesLoading(true);
      setBusinessTypesError('');

      try {
        const response = await getBusinessTypes(token);
        const activeTypes = (response.data || []).filter((item) => item.status === 'active');

        if (!ignore) {
          setBusinessTypes(activeTypes);
          setFormValues((currentValues) => ({
            ...currentValues,
            system_type: currentValues.system_type || activeTypes[0]?.slug || '',
          }));
        }
      } catch (requestError) {
        if (!ignore) {
          setBusinessTypesError(requestError.message);
        }
      } finally {
        if (!ignore) {
          setBusinessTypesLoading(false);
        }
      }
    }

    loadBusinessTypes();

    return () => {
      ignore = true;
    };
  }, [token]);

  const filteredBusinesses = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      return businesses;
    }

    return businesses.filter((item) => item.name.toLowerCase().includes(keyword));
  }, [businesses, search]);

  const rows = filteredBusinesses.map((item, index) => ({
    id: item.id,
    number: index + 1,
    logo: item.logo || '—',
    name: item.name,
    system_type: item.system_type,
    branch_count: Number(item.branch_count || 0),
    status: item.status,
    portal_access: item.portal_access ?? item.portal_state ?? 'open',
  }));

  const columns = [
    { key: 'number', label: 'No.' },
    { key: 'logo', label: 'Logo' },
    { key: 'name', label: 'Name' },
    { key: 'system_type', label: 'System Type' },
    {
      key: 'branch_count',
      label: 'Branches',
      render: (row) => (
        <span className="inline-flex rounded-full border border-[#ece6df] bg-[#fcfbf9] px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#6f6258]">
          {row.branch_count}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${
            row.status === 'active'
              ? 'bg-[#edf7ef] text-[#4e7b56]'
              : row.status === 'under_construction'
                ? 'bg-[#fff4df] text-[#a36a18]'
                : 'bg-[#f5f2ee] text-[#93857b]'
          }`}
        >
          {row.status.replace('_', ' ')}
        </span>
      ),
    },
    {
      key: 'portal_access',
      label: 'Portal Access',
      render: (row) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${
            row.portal_access === 'open'
              ? 'bg-[#edf7ef] text-[#4e7b56]'
              : row.portal_access === 'blurred'
                ? 'bg-[#f2efff] text-[#6657a8]'
                : 'bg-[#f4efea] text-[#7d6858]'
          }`}
        >
          {row.portal_access}
        </span>
      ),
    },
  ];

  if (isSuperadmin) {
    columns.push({
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <TableActionButtons
          canView
          canEdit
          canDelete
          onView={() => openBranchesModal(row.id)}
          onEdit={() => openEditModal(row.id)}
          onDelete={() => handleDeleteBusiness(row.id)}
        />
      ),
    });
  }

  function openModal() {
    setEditingBusinessId(null);
    setSubmitError('');
    setFormValues({
      name: '',
      system_type: businessTypes[0]?.slug || '',
      status: 'active',
      portal_access: 'open',
      initial_branch_name: 'Main Branch',
    });
    setIsModalOpen(true);
  }

  async function loadBranches(businessId) {
    setBranchesLoading(true);
    setBranchError('');

    try {
      const response = await getBranches(token, businessId);
      setBranches(response.data || []);
    } catch (requestError) {
      setBranchError(requestError.message);
    } finally {
      setBranchesLoading(false);
    }
  }

  async function openBranchesModal(businessId) {
    const selectedBusiness = businesses.find((item) => item.id === businessId);

    if (!selectedBusiness) {
      return;
    }

    setSelectedBusinessForBranches(selectedBusiness);
    setEditingBranchId(null);
    setBranchFormValues({
      name: '',
      status: 'active',
    });
    await loadBranches(businessId);
  }

  const activeBranchCount = branches.filter((item) => item.status === 'active').length;
  const inactiveBranchCount = branches.filter((item) => item.status === 'inactive').length;

  function closeBranchesModal() {
    if (branchSubmitting) {
      return;
    }

    setSelectedBusinessForBranches(null);
    setBranches([]);
    setBranchError('');
    setEditingBranchId(null);
    setBranchFormValues({
      name: '',
      status: 'active',
    });
  }

  function openEditBranch(branchId) {
    const branch = branches.find((item) => item.id === branchId);

    if (!branch) {
      return;
    }

    setEditingBranchId(branch.id);
    setBranchFormValues({
      name: branch.name || '',
      status: branch.status || 'active',
    });
  }

  function openEditModal(businessId) {
    const selectedBusiness = businesses.find((item) => item.id === businessId);

    if (!selectedBusiness) {
      return;
    }

    setEditingBusinessId(selectedBusiness.id);
    setSubmitError('');
    setFormValues({
      name: selectedBusiness.name || '',
      system_type: selectedBusiness.system_type || businessTypes[0]?.slug || '',
      status: selectedBusiness.status || 'active',
      portal_access: selectedBusiness.portal_access || selectedBusiness.portal_state || 'open',
      initial_branch_name: '',
    });
    setIsModalOpen(true);
  }

  function closeModal(forceClose = false) {
    if (submitting && !forceClose) {
      return;
    }

    setIsModalOpen(false);
    setEditingBusinessId(null);
    setSubmitError('');
    setSubmitting(false);
    setFormValues({
      name: '',
      system_type: businessTypes[0]?.slug || '',
      status: 'active',
      portal_access: 'open',
      initial_branch_name: 'Main Branch',
    });
  }

  function handleChange(event) {
    const { name, value } = event.target;

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
  }

  function handleBranchChange(event) {
    const { name, value } = event.target;

    setBranchFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
  }

  async function handleDeleteBusiness(businessId) {
    const selectedBusiness = businesses.find((item) => item.id === businessId);

    if (!selectedBusiness) {
      return;
    }
    setDeleteTarget(selectedBusiness);
  }

  function closeDeleteModal() {
    if (deleting) {
      return;
    }

    setDeleteTarget(null);
  }

  async function confirmDeleteBusiness() {
    if (!deleteTarget) {
      return;
    }

    setDeleting(true);

    try {
      await deleteBusiness(token, deleteTarget.id);
      await onBusinessCreated();
      setDeleteTarget(null);
    } catch (requestError) {
      setSubmitError(requestError.message);
    } finally {
      setDeleting(false);
    }
  }

  async function handleBranchSubmit(event) {
    event.preventDefault();

    if (!selectedBusinessForBranches) {
      return;
    }

    if (!branchFormValues.name.trim()) {
      setBranchError('Branch name is required.');
      return;
    }

    setBranchSubmitting(true);
    setBranchError('');

    try {
      const payload = {
        customer_id: selectedBusinessForBranches.id,
        name: branchFormValues.name.trim(),
        status: branchFormValues.status,
      };

      if (editingBranchId) {
        await updateBranch(token, editingBranchId, payload);
      } else {
        await createBranch(token, payload);
      }

      setEditingBranchId(null);
      setBranchFormValues({
        name: '',
        status: 'active',
      });
      await loadBranches(selectedBusinessForBranches.id);
    } catch (requestError) {
      setBranchError(requestError.message);
    } finally {
      setBranchSubmitting(false);
    }
  }

  async function handleToggleBranchStatus(branchId) {
    const selectedBranch = branches.find((item) => item.id === branchId);

    if (!selectedBranch || !selectedBusinessForBranches) {
      return;
    }

    setBranchSubmitting(true);
    setBranchError('');

    try {
      await updateBranch(token, branchId, {
        customer_id: selectedBusinessForBranches.id,
        name: selectedBranch.name,
        status: selectedBranch.status === 'active' ? 'inactive' : 'active',
      });
      await loadBranches(selectedBusinessForBranches.id);
    } catch (requestError) {
      setBranchError(requestError.message);
    } finally {
      setBranchSubmitting(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!formValues.name.trim()) {
      setSubmitError('Business name is required.');
      return;
    }

    if (!formValues.system_type) {
      setSubmitError('Please select a system type.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const payload = {
        name: formValues.name.trim(),
        system_type: formValues.system_type,
        status: formValues.status,
        portal_access: formValues.portal_access,
      };

      if (editingBusinessId) {
        await updateBusiness(token, editingBusinessId, payload);
      } else {
        payload.initial_branch_name = formValues.initial_branch_name.trim() || 'Main Branch';
        const response = await createBusiness(token, payload);
        setCreatedSetup(response.data || null);
      }

      closeModal(true);
      await onBusinessCreated();
    } catch (requestError) {
      setSubmitError(requestError.message);
      setSubmitting(false);
      return;
    }
  }

  const modalTitle = editingBusinessId ? 'Edit Business' : 'Add Business';
  const modalDescription = editingBusinessId
    ? 'Update the tenant business details and keep its system type aligned.'
    : 'Create the business and its first branch. The shared admin account will be created once, then reused.';
  const submitLabel = editingBusinessId
    ? submitting
      ? 'Saving Changes...'
      : 'Save Changes'
    : submitting
      ? 'Saving...'
      : 'Save Business';

  return (
    <>
      <div className="mb-5 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h2 className="m-0 text-2xl font-semibold tracking-[-0.03em] text-[#111111] sm:text-3xl">
            Business List
          </h2>
          <p className="mt-2 text-[#93857b]">
            Manage tenant businesses and their assigned system types.
          </p>
        </div>
        {isSuperadmin ? (
          <Button className="w-full justify-center px-4 py-3 sm:w-auto" onClick={openModal}>
            Add Business
          </Button>
        ) : null}
      </div>

      <div className="mb-[18px]">
        {createdSetup ? (
          <div className="mb-4 rounded-3xl border border-[#e6ddd4] bg-white px-5 py-5 shadow-[0_10px_28px_rgba(69,48,33,0.05)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-[#111111]">Business setup created</h3>
                <p className="mt-2 text-sm text-[#93857b]">
                  The business, its first branch, and the shared admin linkage were processed successfully.
                </p>
              </div>
              <button
                className="text-sm font-medium text-[#7f736b] transition hover:text-[#111111]"
                type="button"
                onClick={() => setCreatedSetup(null)}
              >
                Dismiss
              </button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <div className="rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-4 py-4">
                <p className="text-xs uppercase tracking-[0.08em] text-[#93857b]">Business</p>
                <p className="mt-2 font-semibold text-[#111111]">{createdSetup.business?.name}</p>
              </div>
              <div className="rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-4 py-4">
                <p className="text-xs uppercase tracking-[0.08em] text-[#93857b]">Initial Branch</p>
                <p className="mt-2 font-semibold text-[#111111]">{createdSetup.initial_branch?.name}</p>
              </div>
              <div className="rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-4 py-4">
                <p className="text-xs uppercase tracking-[0.08em] text-[#93857b]">Shared Admin</p>
                <p className="mt-2 font-semibold text-[#111111]">{createdSetup.shared_admin?.username || '—'}</p>
                <p className="mt-1 text-sm text-[#93857b]">
                  {createdSetup.shared_admin?.action === 'created'
                    ? `Created once with temporary password: ${createdSetup.shared_admin?.default_password || '@dm1n'}`
                    : 'Existing shared admin was attached to this business.'}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        <input
          className="w-full rounded-[14px] border border-[#e5dfd8] bg-white px-[18px] py-[14px] text-[#111111] outline-none placeholder:text-[#a1968d] focus:border-[#8e7663] focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)] sm:max-w-[420px]"
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name..."
        />
      </div>

      <Card className="p-4 sm:p-6">
        <div className="flex min-h-[420px] flex-col">
          <Table
            columns={columns}
            rows={rows}
            emptyState={{
              icon: Building2,
              title: 'No businesses yet',
              message: 'Tenant businesses will appear here once they are added to the platform.',
              showAction: false,
            }}
          />
        </div>
      </Card>

      {isModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-[520px] overflow-y-auto rounded-3xl border border-[#e6ddd4] bg-white p-5 shadow-[0_20px_50px_rgba(17,17,17,0.12)] sm:p-6">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h3 className="m-0 text-2xl font-semibold text-[#111111]">{modalTitle}</h3>
                <p className="mt-2 text-sm text-[#93857b]">{modalDescription}</p>
              </div>
              <button
                className="rounded-full border border-[#e5dfd8] p-2 text-[#7f736b] transition hover:bg-[#faf7f3]"
                type="button"
                onClick={closeModal}
                aria-label="Close business form"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form className="grid gap-4" onSubmit={handleSubmit}>
              <label className="grid gap-2">
                <span className="text-sm font-medium text-[#111111]">Business Name</span>
                <input
                  className="rounded-[14px] border border-[#e5dfd8] bg-white px-4 py-3 text-[#111111] outline-none placeholder:text-[#a1968d] focus:border-[#8e7663] focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                  type="text"
                  name="name"
                  value={formValues.name}
                  onChange={handleChange}
                  placeholder="Enter business name"
                />
                <span className="text-sm text-[#93857b]">
                  Use the official business or company name that should appear across the portal.
                </span>
              </label>

              <label className="grid gap-2">
                <span className="text-sm font-medium text-[#111111]">System Type</span>
                <select
                  className="rounded-[14px] border border-[#e5dfd8] bg-white px-4 py-3 text-[#111111] outline-none focus:border-[#8e7663] focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                  name="system_type"
                  value={formValues.system_type}
                  onChange={handleChange}
                  disabled={businessTypesLoading || !businessTypes.length}
                >
                  {businessTypes.map((option) => (
                    <option key={option.id} value={option.slug}>
                      {option.name}
                    </option>
                  ))}
                </select>
                {businessTypesLoading ? (
                  <span className="text-sm text-[#93857b]">Loading system types...</span>
                ) : null}
                {businessTypesError ? (
                  <span className="text-sm text-[#b4533f]">{businessTypesError}</span>
                ) : null}
                {!businessTypesLoading && !businessTypesError && !businessTypes.length ? (
                  <span className="text-sm text-[#93857b]">No active business types available yet.</span>
                ) : null}
                {!businessTypesLoading && businessTypes.length ? (
                  <span className="text-sm text-[#93857b]">
                    Choose the business category this tenant belongs to.
                  </span>
                ) : null}
              </label>

              <label className="grid gap-2">
                <span className="text-sm font-medium text-[#111111]">Status</span>
                <select
                  className="rounded-[14px] border border-[#e5dfd8] bg-white px-4 py-3 text-[#111111] outline-none focus:border-[#8e7663] focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                  name="status"
                  value={formValues.status}
                  onChange={handleChange}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-[#93857b]">
                  Set whether the business is live, inactive, or still in development.
                </span>
              </label>

              <label className="grid gap-2">
                <span className="text-sm font-medium text-[#111111]">Portal Access</span>
                <select
                  className="rounded-[14px] border border-[#e5dfd8] bg-white px-4 py-3 text-[#111111] outline-none focus:border-[#8e7663] focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                  name="portal_access"
                  value={formValues.portal_access}
                  onChange={handleChange}
                >
                  {PORTAL_ACCESS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-[#93857b]">
                  Use open for accessible portals and locked for businesses that are not available yet.
                </span>
              </label>

              {!editingBusinessId ? (
                <label className="grid gap-2">
                  <span className="text-sm font-medium text-[#111111]">Initial Branch</span>
                  <input
                    className="rounded-[14px] border border-[#e5dfd8] bg-white px-4 py-3 text-[#111111] outline-none placeholder:text-[#a1968d] focus:border-[#8e7663] focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                  type="text"
                  name="initial_branch_name"
                  value={formValues.initial_branch_name}
                      onChange={handleChange}
                      placeholder="Main Branch"
                    />
                  <span className="text-sm text-[#93857b]">
                    The system will create the shared admin once, then attach that same admin to future businesses.
                  </span>
                </label>
              ) : null}

              {submitError ? (
                <div className="rounded-2xl border border-[#f0d4cb] bg-[#fff8f6] px-4 py-3 text-sm text-[#b4533f]">
                  {submitError}
                </div>
              ) : null}

              <div className="mt-2 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  className="w-full justify-center sm:w-auto"
                  variant="secondary"
                  type="button"
                  onClick={closeModal}
                >
                  Cancel
                </Button>
                <Button className="w-full justify-center sm:w-auto" type="submit" disabled={submitting}>
                  {submitLabel}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        title="Delete Business"
        description={
          deleteTarget
            ? `Delete "${deleteTarget.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={deleting}
        onConfirm={confirmDeleteBusiness}
        onCancel={closeDeleteModal}
      />

      {selectedBusinessForBranches ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-[820px] overflow-y-auto rounded-3xl border border-[#e6ddd4] bg-white p-5 shadow-[0_20px_50px_rgba(17,17,17,0.12)] sm:p-6">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h3 className="m-0 text-2xl font-semibold text-[#111111]">Branches</h3>
                <p className="mt-2 text-sm text-[#93857b]">
                  Manage branch locations for <span className="font-medium text-[#111111]">{selectedBusinessForBranches.name}</span>.
                </p>
              </div>
              <button
                className="rounded-full border border-[#e5dfd8] p-2 text-[#7f736b] transition hover:bg-[#faf7f3]"
                type="button"
                onClick={closeBranchesModal}
                aria-label="Close branches modal"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_340px]">
              <Card className="p-4 sm:p-6">
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-[#111111]">Branch List</h4>
                  <p className="mt-1 text-sm text-[#93857b]">Each branch belongs to this single business.</p>
                </div>

                <div className="mb-4 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-4 py-4">
                    <p className="text-xs uppercase tracking-[0.08em] text-[#93857b]">Total</p>
                    <p className="mt-2 text-2xl font-semibold text-[#111111]">{branches.length}</p>
                  </div>
                  <div className="rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-4 py-4">
                    <p className="text-xs uppercase tracking-[0.08em] text-[#93857b]">Active</p>
                    <p className="mt-2 text-2xl font-semibold text-[#111111]">{activeBranchCount}</p>
                  </div>
                  <div className="rounded-2xl border border-[#ece6df] bg-[#fcfbf9] px-4 py-4">
                    <p className="text-xs uppercase tracking-[0.08em] text-[#93857b]">Inactive</p>
                    <p className="mt-2 text-2xl font-semibold text-[#111111]">{inactiveBranchCount}</p>
                  </div>
                </div>

                {branchesLoading ? (
                  <div className="rounded-2xl border border-[#e6ddd4] bg-[#fcfbf9] px-4 py-4 text-[#8b7a6d]">
                    Loading branches...
                  </div>
                ) : (
                  <Table
                    columns={[
                      { key: 'id', label: 'No.' },
                      { key: 'name', label: 'Branch Name' },
                      {
                        key: 'status',
                        label: 'Status',
                        render: (row) => (
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${
                              row.status === 'active'
                                ? 'bg-[#edf7ef] text-[#4e7b56]'
                                : 'bg-[#f5f2ee] text-[#93857b]'
                            }`}
                          >
                            {row.status}
                          </span>
                        ),
                      },
                      {
                        key: 'actions',
                        label: 'Actions',
                        render: (row) => (
                          <TableActionButtons
                            canEdit
                            canToggle
                            onEdit={() => openEditBranch(row.id)}
                            onToggle={() => handleToggleBranchStatus(row.id)}
                            toggleStatus={row.status}
                          />
                        ),
                      },
                    ]}
                    rows={branches}
                    emptyState={{
                      icon: Building2,
                      title: 'No branches yet',
                      message: 'Add the next branch location for this business here.',
                      showAction: false,
                    }}
                  />
                )}
              </Card>

              <Card className="p-4 sm:p-6">
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-[#111111]">
                    {editingBranchId ? 'Edit Branch' : 'Add Branch'}
                  </h4>
                  <p className="mt-1 text-sm text-[#93857b]">
                    Use one business with multiple branches when the owner has several locations.
                  </p>
                </div>

                <form className="grid gap-4" onSubmit={handleBranchSubmit}>
                  <label className="grid gap-2">
                    <span className="text-sm font-medium text-[#111111]">Branch Name</span>
                    <input
                      className="rounded-[14px] border border-[#e5dfd8] bg-white px-4 py-3 text-[#111111] outline-none placeholder:text-[#a1968d] focus:border-[#8e7663] focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                      type="text"
                      name="name"
                      value={branchFormValues.name}
                      onChange={handleBranchChange}
                      placeholder="Enter branch name"
                    />
                    <span className="text-sm text-[#93857b]">
                      Example: Main Branch, Port Branch, Downtown Branch.
                    </span>
                  </label>

                  <label className="grid gap-2">
                    <span className="text-sm font-medium text-[#111111]">Status</span>
                    <select
                      className="rounded-[14px] border border-[#e5dfd8] bg-white px-4 py-3 text-[#111111] outline-none focus:border-[#8e7663] focus:shadow-[0_0_0_4px_rgba(142,118,99,0.10)]"
                      name="status"
                      value={branchFormValues.status}
                      onChange={handleBranchChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <span className="text-sm text-[#93857b]">
                      Inactive branches stay in the list but are not treated as operational.
                    </span>
                  </label>

                  {branchError ? (
                    <div className="rounded-2xl border border-[#f0d4cb] bg-[#fff8f6] px-4 py-3 text-sm text-[#b4533f]">
                      {branchError}
                    </div>
                  ) : null}

                  <div className="mt-2 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    {editingBranchId ? (
                      <Button
                        className="w-full justify-center sm:w-auto"
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          setEditingBranchId(null);
                          setBranchFormValues({ name: '', status: 'active' });
                          setBranchError('');
                        }}
                      >
                        Cancel
                      </Button>
                    ) : null}
                    <Button className="w-full justify-center sm:w-auto" type="submit" disabled={branchSubmitting}>
                      {branchSubmitting
                        ? 'Saving...'
                        : editingBranchId
                          ? 'Save Branch'
                          : 'Add Branch'}
                    </Button>
                  </div>
                </form>
              </Card>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default BusinessList;
